import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';

const AccountSettingsPage: React.FC = () => {
  const { isAuthenticated, user, updateUserState, refreshProfile } = useAuth();

  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setFullName(user.full_name || '');
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && !user) {
      void refreshProfile();
    }
  }, [isAuthenticated, user, refreshProfile]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!currentPassword) {
      setError('Current password is required.');
      return;
    }

    if (newPassword && newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    if (newPassword && newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    setSaving(true);
    try {
      const response = await authService.updateAccount({
        username: username.trim(),
        full_name: fullName.trim(),
        current_password: currentPassword,
        new_password: newPassword || undefined,
      });

      updateUserState(response.data.user);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Account updated successfully.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Failed to update account.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="page-shell container">
        <div className="auth-panel">
          <h3 className="auth-heading">Sign in required</h3>
          <p className="auth-copy">Please sign in to manage account settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell container">
      <div className="card settings-card">
        <div className="page-intro">
          <div>
            <p className="eyebrow">Account security</p>
            <h2 className="page-heading">Update account details</h2>
            <p className="page-copy">Change username, full name, and password securely. Current password is required for all account changes.</p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit} className="module-card">
          <div className="form-grid two-column">
            <div className="form-group">
              <label htmlFor="account-username" className="form-label">Username</label>
              <input
                id="account-username"
                className="form-input"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="account-full-name" className="form-label">Full name</label>
              <input
                id="account-full-name"
                className="form-input"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="account-current-password" className="form-label">Current password</label>
              <input
                id="account-current-password"
                type="password"
                className="form-input"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="account-new-password" className="form-label">New password (optional)</label>
              <input
                id="account-new-password"
                type="password"
                className="form-input"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="account-confirm-password" className="form-label">Confirm new password</label>
              <input
                id="account-confirm-password"
                type="password"
                className="form-input"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </div>
          </div>

          <div className="action-row" style={{ marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save account changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
